import { useFormatter } from '../utils/formatters';
import TypeAheadLookup from '../components/TypeAheadLookup';
import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import AppShell from '../components/layout/AppShell';
import HelpButton from './Guides/HelpButton';
import {
  PageHeader, DataTableToolbar, PaginationBar, ActiveBadge,
  EmptyState, LoadingSpinner
} from '../components/layout/SharedComponents';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import ContractEditor from '../components/ContractEditor';
import InteractiveTable from '../components/InteractiveTable';
import api from '../services/api';
import { toast } from 'sonner';
import { TemplatePicker, SupplierSelect, ContractManagerSelect, ApprovalRouteBuilder, ContractDetailsStep, ContractWritingStep, ApprovalHistory } from './SupplierContracts/components/ContractHelpers';
import { NewContractWizard } from './SupplierContracts/components/NewContractWizard';
import { ExistingContractModal } from './SupplierContracts/components/ExistingContractModal';
import {
  Plus, Download, Eye, Trash2, Upload, Send, CheckCircle,
  XCircle, FileEdit, Clock, AlertTriangle, FilePlus,
  FileText, Wand2, ChevronRight, ArrowLeft, BookOpen
} from 'lucide-react';
import { EMPTY_FORM, STATUS_CONFIG, WorkflowBadge, CAT_COLOR } from './SupplierContracts/contractConstants';
import ModuleReports from './Reports/ModuleReports';
import { CONTRACT_REPORTS } from './Reports/reportConfigs';
import ContractDashboard from './Contracts/ContractDashboard';
import { useAuth } from '../contexts/AuthContext';

// ── Category colour map ─────────────────────────────────────────────────────

// ═══════════════════════════════════════════════════════════════════════════
// STEP 1 — Template Picker
// ═══════════════════════════════════════════════════════════════════════════

// ═══════════════════════════════════════════════════════════════════════════
// Helper: Supplier search combobox
// ═══════════════════════════════════════════════════════════════════════════

// Helper: Contract manager dropdown from users DB

// Helper: Approval route builder

// ═══════════════════════════════════════════════════════════════════════════
// STEP 2 — Contract Details (quick form, smart-field aware)
// ═══════════════════════════════════════════════════════════════════════════



// ═══════════════════════════════════════════════════════════════════════════
// STEP 3 — Contract Writing (editor + smart fields already applied)
// ═══════════════════════════════════════════════════════════════════════════

// ═══════════════════════════════════════════════════════════════════════════
// Approval History
// ═══════════════════════════════════════════════════════════════════════════

// ═══════════════════════════════════════════════════════════════════════════
// NEW CONTRACT WIZARD  (3 steps for brand-new contracts)
// ═══════════════════════════════════════════════════════════════════════════

// ═══════════════════════════════════════════════════════════════════════════
// EXISTING CONTRACT EDITOR  (tabbed view for editing saved contracts)
// ═══════════════════════════════════════════════════════════════════════════

// ═══════════════════════════════════════════════════════════════════════════
// MAIN LIST PAGE
// ═══════════════════════════════════════════════════════════════════════════
export default function SupplierContractsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get('tab') || 'dashboard';
  const setActiveTab = (tab) => setSearchParams({ tab });
  return (
    <AppShell>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <h1 className="text-xl font-bold" style={{ fontFamily: 'Space Grotesk' }}>Supplier Contracts</h1>
          <HelpButton guideId="contracts" label="Contracts Guide" />
        </div>
      </div>
      <div className="flex gap-1 border-b mb-4" data-testid="contract-tabs">
        {[{ id: 'dashboard', label: 'Analytics' }, { id: 'list', label: 'Contracts' }, { id: 'reports', label: 'Reports', icon: FileText }].map(t => (
          <button key={t.id} onClick={() => setActiveTab(t.id)} data-testid={`contract-tab-${t.id}`}
            className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeTab === t.id ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}>
            {t.icon && <t.icon className="h-3.5 w-3.5" />} {t.label}
          </button>
        ))}
      </div>
      {activeTab === 'dashboard' && <ContractDashboard />}
      {activeTab === 'list' && <ContractsListContent />}
      {activeTab === 'reports' && <ModuleReports reports={CONTRACT_REPORTS} apiPrefix="/api/module-reports/contracts" moduleLabel="Contracts" />}
    </AppShell>
  );
}

function ContractsListContent() {
  const [records, setRecords] = useState([]);
  const { fmt } = useFormatter();
  const { systemSettings } = useAuth();
  const tz = systemSettings?.timezone || 'America/Los_Angeles';
  const [searchParams] = useSearchParams();
  const [sortState, setSortState] = useState({ field: 'contract_number', dir: 'asc' });
  const handleSort = useCallback((field) => {
    setSortState(prev => prev.field === field ? { field, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { field, dir: 'asc' });
  }, []);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || '');
  const [activeFilter, setActiveFilter] = useState(searchParams.get('active') || '');
  const [loading, setLoading] = useState(false);
  const [newOpen, setNewOpen] = useState(false);
  const [editContract, setEditContract] = useState(searchParams.get('open') || null);

  // Sync filter state when URL searchParams change (dashboard drill-down)
  useEffect(() => {
    const sp_search = searchParams.get('search') || '';
    const sp_status = searchParams.get('status') || '';
    const sp_active = searchParams.get('active') || '';
    const sp_open = searchParams.get('open') || null;
    if (sp_search !== search) setSearch(sp_search);
    if (sp_status !== statusFilter) setStatusFilter(sp_status);
    if (sp_active !== activeFilter) setActiveFilter(sp_active);
    if (sp_open !== editContract) setEditContract(sp_open);
    setPage(1);
  }, [searchParams]); // eslint-disable-line react-hooks/exhaustive-deps

  const defaultCols = useMemo(() => [
    { key: 'contract_number', label: 'Contract Number', width: 150, sortField: 'contract_number', visible: true },
    { key: 'description', label: 'Description', width: 200, sortField: 'description', visible: true },
    { key: 'workflow_status', label: 'Status', width: 150, sortField: 'workflow_status', visible: true },
    { key: 'contract_end', label: 'Contract End', width: 130, sortField: 'contract_end', visible: true },
    { key: 'active', label: 'Active', width: 80, sortField: 'active', visible: true },
    { key: 'contract_value', label: 'Value', width: 130, sortField: 'contract_value', align: 'right', visible: true },
    { key: 'contract_manager', label: 'Manager', width: 150, sortField: 'contract_manager', visible: true },
    { key: 'supplier_name', label: 'Supplier', width: 180, sortField: 'supplier_name', visible: true },
  ], []);

  const fmtDate = d => { if (!d) return '—'; try { return new Date(d).toLocaleDateString('en-GB', { day:'2-digit', month:'short', year:'numeric', timeZone: tz }); } catch { return d; } };

  const renderCell = useCallback((col, r) => {
    switch (col.key) {
      case 'contract_number': return <span className="font-semibold text-zinc-900 text-sm">{r.contract_number}</span>;
      case 'description': return <span className="text-sm max-w-[180px] truncate block">{r.description}</span>;
      case 'workflow_status': return <WorkflowBadge status={r.workflow_status || 'DRAFT'} reviewStage={r.review_stage} stageLabel={r.review_stage_label} />;
      case 'contract_end': return <span className="text-sm whitespace-nowrap">{fmtDate(r.contract_end)}</span>;
      case 'active': return <ActiveBadge active={r.active} />;
      case 'contract_value': return <span className="text-sm tabular-nums">{fmt(r.contract_value)}</span>;
      case 'contract_manager': return <span className="text-sm">{r.contract_manager}</span>;
      case 'supplier_name': return <span className="text-sm max-w-[150px] truncate block">{r.supplier_name}</span>;
      default: return <span className="text-sm">{r[col.key] != null ? String(r[col.key]) : '—'}</span>;
    }
  }, [fmt]); // contract_number string

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { search, page, page_size: pageSize, sort_by: sortState.field, sort_dir: sortState.dir };
      if (statusFilter) params.status = statusFilter;
      if (activeFilter) params.active = activeFilter;
      const res = await api.get('/api/data/supplier-contracts', { params });
      setRecords(res.data.data || []); setTotal(res.data.total);
    } catch (err) { if (err?.response?.status >= 500) toast.error('Failed to load contracts'); }
    setLoading(false);
  }, [search, page, pageSize, sortState, statusFilter, activeFilter]);

  useEffect(() => { load(); }, [load]);

  const hasDashboardFilter = !!(searchParams.get('search') || searchParams.get('open') || searchParams.get('status') || searchParams.get('active'));
  const filterLabel = searchParams.get('label') || '';
  const clearDashboardFilters = () => {
    setSearch(''); setStatusFilter(''); setActiveFilter(''); setEditContract(null);
    window.history.replaceState(null, '', '/data/contracts?tab=list');
  };

  return (
    <>
      {hasDashboardFilter && (
        <div className="flex items-center gap-2 px-3 py-1.5 bg-blue-50 border border-blue-200 rounded-lg text-xs text-blue-700 mb-3">
          <span className="font-medium">Filtered{filterLabel ? `: ${filterLabel}` : ''}</span>
          <button onClick={clearDashboardFilters} className="ml-1 hover:text-blue-900 font-bold" data-testid="clear-contract-filter">&times;</button>
        </div>
      )}
      <PageHeader
        description={`Total records: ${total}`}
        actions={<>
          <Button size="sm" variant="outline"><Download className="h-4 w-4 mr-1" />Export</Button>
          <Button size="sm" onClick={() => setNewOpen(true)} data-testid="contracts-add-button">
            <FilePlus className="h-4 w-4 mr-1" />New Contract
          </Button>
        </>}
      />
      <DataTableToolbar
        searchValue={search} onSearch={v => { setSearch(v); setPage(1); }}
        onClear={() => { setSearch(''); setPage(1); }} searchPlaceholder="Search contracts..."
 toolbarSlotId="contracts-listing-toolbar" />
      <div className="rounded-xl border bg-card">
        {loading ? <div className="py-12"><LoadingSpinner /></div> :
        records.length === 0 ? (
          <EmptyState title="No contracts"
            description="Click 'New Contract' to choose a template and start writing."
            action={<Button size="sm" onClick={() => setNewOpen(true)}>New Contract</Button>}
          />
        ) : (
          <InteractiveTable
            tableId="contracts-listing"
            toolbarPortalId="contracts-listing-toolbar"
            defaultCols={defaultCols}
            data={records}
            renderCell={renderCell}
            onRowClick={(row) => setEditContract(row.contract_number)}
            sortState={sortState}
            onSort={handleSort}
            rowKey="contract_number"
          />
        )}
        <div className="px-4">
          <PaginationBar page={page} pageSize={pageSize} total={total}
            onPageChange={setPage} onPageSizeChange={v => { setPageSize(v); setPage(1); }} />
        </div>
      </div>

      {/* New Contract Wizard */}
      <NewContractWizard
        open={newOpen}
        onClose={() => setNewOpen(false)}
        onCreated={load}
      />

      {/* Edit Existing Contract */}
      {editContract && (
        <ExistingContractModal
          open={!!editContract}
          onClose={() => setEditContract(null)}
          contractNumber={editContract}
          onSaved={load}
        />
      )}
    </>
  );
}
